import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Welcome.css';

const Welcome = ({ onLogin }) => {
  const [expandedFeature, setExpandedFeature] = useState(null);

  const features = [
    {
      id: 'instant',
      icon: '⚡',
      title: 'Instant Generation',
      summary: 'Create personalized learning activities in seconds, not hours',
      details: 'Our AI-powered system generates custom educational content instantly. No more spending hours researching and creating activities - just input your preferences and get professionally designed learning materials ready for your child.'
    },
    {
      id: 'age-appropriate',
      icon: '🎯',
      title: 'Age-Appropriate Content',
      summary: 'Activities automatically adjusted for your child\'s grade level and learning style',
      details: 'Every activity is carefully calibrated for developmental appropriateness. Our system considers cognitive abilities, reading levels, and learning preferences to ensure content is challenging but achievable for your child\'s specific grade level.'
    },
    {
      id: 'multiple-subjects',
      icon: '🔬',
      title: 'Multiple Subjects',
      summary: 'Cover math, science, reading, writing, history, and more with diverse activity types',
      details: 'From STEM subjects like mathematics and science to humanities like literature and history, we cover the full spectrum of educational topics. Each subject includes various activity types: worksheets, experiments, creative projects, and interactive exercises.'
    },
    {
      id: 'customizable',
      icon: '🌟',
      title: 'Customizable',
      summary: 'Tailor activities to your child\'s interests, learning goals, and educational preferences',
      details: 'Personalization is key to engagement. Set specific learning objectives, incorporate your child\'s favorite topics, adjust difficulty levels, and choose activity formats that work best for your homeschool environment and teaching style.'
    },
    {
      id: 'track-history',
      icon: '📈',
      title: 'Track History',
      summary: 'Keep a history of activities generated along your journey',
      details: 'Easily access a record of all the activities you\'ve generated. Review past contents and revisit favorite activities whenever you like.'
    },
    {
      id: 'family-friendly',
      icon: '🏠',
      title: 'Family-Friendly',
      summary: 'Designed specifically for homeschooling families with multiple children and varying needs',
      details: 'Perfect for families with children of different ages and learning styles. Create activities for multiple grade levels, manage different learning paths, and generate content that can be adapted for siblings or group learning sessions.'
    }
  ];

  const toggleFeature = (featureId) => {
    setExpandedFeature(expandedFeature === featureId ? null : featureId);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="welcome-container">
      <header className="welcome-header">
        <div className="header-content">
          <h1 className="site-title">Mabry Education</h1>
          <nav className="welcome-nav">
            <button 
              className="nav-link"
              onClick={() => scrollToSection('features-section')}
            >
              Features
            </button>
            <button 
              className="nav-link"
              onClick={() => scrollToSection('how-it-works-section')}
            >
              How It Works
            </button>
            <Link 
              to="/transparency"
              className="nav-link"
            >
              Transparency
            </Link>
          </nav>
          <button 
            className="login-button"
            onClick={onLogin}
            aria-label="Login to your account"
          >
            Login
          </button>
        </div>
      </header>

      <main className="welcome-main">
        <div className="hero-section">
          <h2 className="hero-title">
            Personalized Homeschool Activity Generator
          </h2>
          <p className="hero-subtitle">
            Create engaging, customized learning activities tailored to your child&apos;s interests and educational needs
          </p>
        </div>

        <div className="features-section" id="features-section">
          <h3>Why Choose Our Activity Generator?</h3>
          <div className="feature-grid">
            {features.map((feature) => (
              <div 
                key={feature.id}
                className={`feature-card ${expandedFeature === feature.id ? 'expanded' : ''}`}
                onClick={() => toggleFeature(feature.id)}
              >
                <div className="feature-header">
                  <div className="feature-icon">{feature.icon}</div>
                  <div className="feature-title-section">
                    <h3>{feature.title}</h3>
                    <div className="expand-indicator">
                      {expandedFeature === feature.id ? '−' : '+'}
                    </div>
                  </div>
                </div>
                <div className="feature-content">
                  <p className="feature-summary">{feature.summary}</p>
                  {expandedFeature === feature.id && (
                    <p className="feature-details">{feature.details}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="how-it-works-section" id="how-it-works-section">
          <h3>How It Works</h3>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Sign Up & Login</h4>
                <p>Create your free account to access the activity generator and track your progress</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Choose Your Topic</h4>
                <p>Select a subject area or let your child pick their favorite topic</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Customize Settings</h4>
                <p>Set age level, difficulty, and any specific learning goals</p>
              </div>
            </div>

            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Generate & Download</h4>
                <p>Get your custom activity as a PDF ready for printing or digital use</p>
              </div>
            </div>
          </div>
        </div>

        <div className="cta-section">
          <h3>Ready to Get Started?</h3>
          <p>Join me in creating amazing learning experiences for work from home and homeschooling parents</p>
          <button 
            className="cta-button"
            onClick={onLogin}
          >
            Start Creating Activities
          </button>
        </div>
      </main>

      <footer className="welcome-footer">
        <p>&copy; 2026 Mabry Education. Empowering homeschool families with AI-generated learning activities.</p>
      </footer>
    </div>
  );
};

export default Welcome;